import { create } from 'zustand';
import { http } from '@/libs/http';
import type { LogEntry } from '@/types';

interface LogState {
    logs: LogEntry[];
    isLoading: boolean;
    error: string | null;
    
    // Core actions
    list: (params?: Record<string, string>, filter?: (logs: LogEntry[]) => LogEntry[]) => Promise<LogEntry[]>;
    exportLogs: (format: 'json' | 'csv' | 'pdf', params?: Record<string, string>) => Promise<void>;
    getSecuritySummary: (hours?: number, limit?: number) => Promise<{ summary: Record<string, number>; events: LogEntry[] }>;
    select: (criteria: (logs: LogEntry[]) => LogEntry) => LogEntry;
}

export const useLogs = create<LogState>((set, get) => ({
    logs: [],
    isLoading: false,
    error: null,

    list: async (params, filter) => {
        set({ isLoading: true, error: null });
        try {
            let url = '/mgmt/v1/logs';
            if (params) {
                const qs = new URLSearchParams(params).toString();
                if (qs) url += `?${qs}`;
            }
            const data = await http.get<LogEntry[]>(url);
            set({ logs: data, isLoading: false });
            return filter ? filter(data) : data;
        } catch (err: any) {
            set({ error: err.message, isLoading: false });
            return [];
        }
    },

    exportLogs: async (format, params) => {
        const qp = new URLSearchParams({ ...(params || {}), format }).toString();
        const baseUrl = import.meta.env.VITE_API_URL || '/api';
        const url = `${baseUrl}/mgmt/v1/logs/export?${qp}`;

        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to export logs');
        }

        const blob = await response.blob();
        const link = document.createElement('a');
        const href = URL.createObjectURL(blob);
        link.href = href;
        link.download = format === 'csv' ? 'audit_logs.csv' : format === 'pdf' ? 'audit_logs.pdf' : 'audit_logs.json';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(href);
    },

    getSecuritySummary: async (hours = 24, limit = 50) => {
        const url = `/mgmt/v1/logs/security-events?hours=${hours}&limit=${limit}`;
        const data = await http.get<{ summary: Record<string, number>; events: LogEntry[] }>(url);
        return data;
    },

    select: (criteria) => {
        return criteria(get().logs);
    },
}));
